import { safeValidateUIMessages } from "ai";
import {
  fetchMessagePage,
  fetchNewestMessages,
  HISTORY_PAGE_SIZE,
  type ThreadEndpoint,
} from "./agent-session";
import { lumeTools } from "./tools";
import type { LumeUIMessage } from "./types";

/**
 * Reading a conversation back, one page at a time.
 *
 * The drawer opens on the newest forty messages and walks backwards from there as
 * the student scrolls up. It used to read forty and stop, silently: a long
 * tutoring thread was cut off at the top with nothing saying so, while the agent
 * carried on remembering the part that was not on screen. So the panel and the
 * agent disagreed again — the same disagreement the server-side transcript was
 * meant to end, just at the other end of the thread.
 *
 * BACKWARDS PAGING IS COUNTED FROM THE OLDEST MESSAGE, not from the newest. Paging
 * back from the newest end is the obvious reading of "the page above this one" and
 * it does not hold still: every reply appends a row, so the window slides by one
 * each time and the page above would arrive overlapping — and, after enough
 * replies, skipping. Offsets from the start of the thread are fixed, whatever is
 * appended while the drawer is open (agent/agent-session.ts).
 *
 * What that costs is an overlap on the FIRST step back, because page boundaries
 * are multiples of forty and the newest page rarely starts on one. Every id this
 * has handed out is remembered, so the repeats are dropped rather than shown
 * twice.
 */
export class ThreadHistory {
  private readonly endpoint: ThreadEndpoint | null;
  private readonly threadId: string | null;

  /** The ascending page to read next, counted from the oldest. Null = exhausted. */
  private nextPage: number | null;

  /** Everything already on screen, so an overlapping page repeats nothing. */
  private readonly seen: Set<string>;

  /** The read in flight, so a burst of scroll events makes one request. */
  private inFlight: Promise<LumeUIMessage[]> | null = null;

  private constructor(
    endpoint: ThreadEndpoint | null,
    threadId: string | null,
    nextPage: number | null,
    seen: Set<string>
  ) {
    this.endpoint = endpoint;
    this.threadId = threadId;
    this.nextPage = nextPage;
    this.seen = seen;
  }

  /**
   * A conversation with nothing behind it.
   *
   * Not an error case: it is what the stub surfaces get, and what a thread whose
   * history could not be read gets. Both have a working chat and no past to
   * scroll into, and neither should make the drawer offer one.
   */
  static none(): ThreadHistory {
    return new ThreadHistory(null, null, null, new Set());
  }

  static opened(
    endpoint: ThreadEndpoint,
    threadId: string,
    nextPage: number | null,
    shown: LumeUIMessage[]
  ): ThreadHistory {
    return new ThreadHistory(
      endpoint,
      threadId,
      nextPage,
      new Set(shown.map((message) => message.id))
    );
  }

  /** Whether there is anything above what is on screen. */
  get hasOlder(): boolean {
    return this.nextPage !== null;
  }

  /**
   * The page above the one on screen, oldest first, ready to be prepended.
   *
   * Empty is a normal answer and not the end: a page can be entirely overlap, or
   * entirely rows the drawer does not render (a turn that was one tool call). So
   * it keeps stepping back until it finds something to show — capped, because a
   * student waiting on a scroll should not sit through an unbounded walk to the
   * start of the conversation.
   */
  loadOlder(): Promise<LumeUIMessage[]> {
    if (this.inFlight !== null) {
      return this.inFlight;
    }

    const read = this.readOlder().finally(() => {
      this.inFlight = null;
    });

    this.inFlight = read;

    return read;
  }

  private async readOlder(): Promise<LumeUIMessage[]> {
    const endpoint = this.endpoint;
    const threadId = this.threadId;

    if (endpoint === null || threadId === null) {
      return [];
    }

    for (let attempt = 0; attempt < PAGES_PER_STEP; attempt += 1) {
      const page = this.nextPage;

      if (page === null) {
        return [];
      }

      const transcript = await fetchMessagePage(endpoint, threadId, page);

      // Unreadable leaves nextPage where it is, so scrolling up again retries the
      // same page rather than stepping over it. A page skipped on a failed request
      // would be a hole in the middle of the conversation.
      if (transcript.status === "unreadable") {
        return [];
      }

      this.nextPage = page === 0 ? null : page - 1;

      const older = (await validateStored(transcript.messages)) ?? [];
      const fresh = older.filter((message) => !this.seen.has(message.id));

      for (const message of fresh) {
        this.seen.add(message.id);
      }

      if (fresh.length > 0) {
        return fresh;
      }
    }

    return [];
  }
}

/** How many pages one scroll to the top may walk through before giving up. */
const PAGES_PER_STEP = 5;

/** The conversation as the drawer should open it, or why it cannot. */
export type OpenedHistory =
  | { status: "read"; messages: LumeUIMessage[]; history: ThreadHistory }
  | { status: "unreadable" };

/**
 * The newest page of a thread, and a cursor for everything before it.
 *
 * Still validated, even though it comes from our service rather than from storage
 * the student could edit: the rows may have been written by an older build whose
 * widget output had a different shape. safeValidateUIMessages checks the tool parts
 * against the current schemas in agent/tools.ts, so an incompatible history cannot
 * render a widget with missing fields.
 */
export async function openThreadHistory(
  endpoint: ThreadEndpoint,
  threadId: string
): Promise<OpenedHistory> {
  const transcript = await fetchNewestMessages(endpoint, threadId);

  if (transcript.status === "unreadable") {
    return { status: "unreadable" };
  }

  // Short-circuited rather than validated: safeValidateUIMessages REJECTS an empty
  // array ("Messages array must not be empty"), so a brand-new thread would come
  // back as a validation failure and be reported unreadable — the "I could not load
  // our conversation" notice, shown to a student who has never spoken to Lume here
  if (transcript.messages.length === 0) {
    return { status: "read", messages: [], history: ThreadHistory.none() };
  }

  const messages = await validateStored(transcript.messages);

  if (messages === null) {
    return { status: "unreadable" };
  }

  return {
    status: "read",
    messages,
    history: ThreadHistory.opened(
      endpoint,
      threadId,
      firstPageBack(transcript.total),
      messages
    ),
  };
}

/**
 * The ascending page holding the message just above the newest page, or null when
 * the whole conversation is already on screen.
 *
 * Counted in STORED ROWS rather than in the messages that survived conversion: a
 * tool-only turn and a system row are dropped on the way in, so counting what is
 * rendered would put the boundary in the wrong place. The rows we hold are the last
 * `HISTORY_PAGE_SIZE` of `total`, so everything before them is `total - size` rows,
 * and the page holding the last of those is what to read next.
 *
 * A null total disables scrolling back entirely. The service does send it, but the
 * documented response schema does not promise it — and guessing "there is probably
 * more" without being able to say WHERE would page from a boundary we made up.
 */
function firstPageBack(total: number | null): number | null {
  if (total === null) {
    return null;
  }

  const older = total - HISTORY_PAGE_SIZE;

  return older > 0 ? Math.floor((older - 1) / HISTORY_PAGE_SIZE) : null;
}

/**
 * Stored rows as messages the drawer can render, or null if they will not validate.
 *
 * TWO ATTEMPTS, because the first one is all-or-nothing. One widget whose output
 * schema has since changed would otherwise throw away an entire conversation, and
 * losing the text over a button is the wrong trade — so a failed validation is
 * retried with the widget parts removed. Only if the text alone will not validate
 * is the page reported unreadable.
 */
async function validateStored(
  rows: unknown[]
): Promise<LumeUIMessage[] | null> {
  // A page can be empty once the overlap and the unrenderable rows are gone, and
  // safeValidateUIMessages rejects an empty array outright — so asking it would
  // report a page with nothing in it as a page that could not be read
  if (rows.length === 0) {
    return [];
  }

  const validated = await safeValidateUIMessages<LumeUIMessage>({
    messages: rows,
    tools: lumeTools,
  });

  if (validated.success) {
    return validated.data;
  }

  console.error("[lume] Stored conversation did not validate", validated.error);

  const withoutWidgets = await safeValidateUIMessages<LumeUIMessage>({
    // Stripped of widgets, a turn can be left with nothing in it — and an empty
    // bubble reads as a reply that failed
    messages: rows.map(textOnly).filter(hasParts),
    tools: lumeTools,
  });

  return withoutWidgets.success ? withoutWidgets.data : null;
}

/** One stored message with its widget parts dropped, keeping the text. */
function textOnly(message: unknown): unknown {
  if (message === null || typeof message !== "object") {
    return message;
  }

  const parts = (message as { parts?: unknown }).parts;

  if (!Array.isArray(parts)) {
    return message;
  }

  return {
    ...message,
    parts: parts.filter(
      (part) => (part as { type?: unknown } | null)?.type === "text"
    ),
  };
}

function hasParts(message: unknown): boolean {
  const parts = (message as { parts?: unknown } | null)?.parts;

  return Array.isArray(parts) && parts.length > 0;
}
